import React, { useState, useEffect, useCallback } from "react";
import { Modal, Input, List, Select, message, Button, Image, Space } from "antd";
import { connect } from "react-redux";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import { Locale, ApiManager, Util } from "../../../utils";
import SimpleProductInfo from "../../../components/product/SimpleProductInfo";
import { ScopeSelectionFooter } from "./ScopeSelectionTags";
import CategoryFilterSelect from "../components/CategoryFilterSelect";
import { JD_ID } from "../../../constants";
import "./coupons.scss";

const { Search } = Input;
const { Option } = Select;
const i18n = Locale.getInstance();
const api = ApiManager.getInstance();
const util = Util.getInstance();

function CouponProductSelectModal({
  visible,
  onCancel,
  user,
  products,
  onSelect,
  disabled = false,
  maxSelection = 0,
}) {
  const [keyword, setKeyword] = useState("");
  const [categoryId, setCategoryId] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState("name");
  const [data, setData] = useState({ data: [], current_page: 1, total: 0 });

  const fetchData = useCallback(
    async (page = 1) => {
      const token = user?.token;
      let customApi = api.product.products;
      if (user?.merchant?.id === JD_ID) {
        customApi = api.product.jdProducts;
      }
      if (!token) {
        return;
      }

      try {
        setLoading(true);
        const res = await customApi.run({
          token,
          query: {
            category_id: categoryId,
            page,
            keyword,
            search_type: searchType,
            count: 5,
          },
        });
        if (res.result) {
          setData(res.data);
        } else {
          message.error(res.message);
        }
      } catch (error) {
        console.log("CouponProductSelectModal -> error", error);
      } finally {
        setLoading(false);
      }
    },
    [categoryId, keyword, searchType, user]
  );

  useEffect(() => {
    if (visible) {
      setCategoryId(-1);
      setKeyword("");
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      fetchData(1);
    }
  }, [visible, categoryId, searchType, fetchData]);

  const checkIfSelected = (item) => {
    return products?.items.indexOf(item.id) !== -1;
  };

  const onSelectItem = (item) => {
    if (disabled) {
      return;
    }

    if (checkIfSelected(item)) {
      onSelect({
        items: products.items.filter((id) => id !== item.id),
        byId: util.omit(products.byId, [item.id]),
      });
      return;
    }

    if (maxSelection === 1) {
      onSelect({
        items: [item.id],
        byId: { [item.id]: item },
      });
      return;
    }

    if (maxSelection > 1 && products.items.length >= maxSelection) {
      message.warning(`最多选择 ${maxSelection} 个商品`);
      return;
    }

    onSelect({
      items: [...products.items, item.id],
      byId: {
        ...products.byId,
        [item.id]: item,
      },
    });
  };

  const removeSelected = (id) => {
    if (disabled) {
      return;
    }
    onSelect({
      items: products.items.filter((itemId) => itemId !== id),
      byId: util.omit(products.byId, [id]),
    });
  };

  const clearSelected = () => {
    if (disabled) {
      return;
    }
    onSelect({ items: [], byId: {} });
  };

  const selectBefore = (
    <Select
      value={searchType}
      onChange={setSearchType}
      style={{ width: 120 }}
    >
      <Option value="name">{i18n.t("product.search.name")}</Option>
      <Option value="product_no">{i18n.t("product.search.product_no")}</Option>
      <Option value="barcode">{i18n.t("product.search.barcode")}</Option>
    </Select>
  );

  return (
    <Modal
      className="coupon-scope-select-modal"
      width={1020}
      title={
        <Space wrap align="center">
          <CategoryFilterSelect
            user={user}
            value={categoryId}
            enabled={visible}
            onChange={setCategoryId}
          />
          <Search
            allowClear
            style={{ width: 400 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            addonBefore={selectBefore}
            onSearch={() => fetchData(1)}
          />
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose={false}
    >
      <div className="coupon-scope-select-modal__list">
        <List
          itemLayout="horizontal"
          dataSource={data.data || []}
          loading={loading}
          pagination={{
            current: data?.current_page,
            total: data?.total,
            showSizeChanger: false,
            showQuickJumper: true,
            pageSize: 5,
            onChange: (page) => fetchData(page),
          }}
          renderItem={(item) => (
            <List.Item
              key={item.id}
              actions={[
                <Button
                  key="select"
                  disabled={disabled}
                  type={checkIfSelected(item) ? "default" : "primary"}
                  onClick={() => onSelectItem(item)}
                >
                  {checkIfSelected(item)
                    ? i18n.t("base.modal.cancelText")
                    : i18n.t("sales.coupons.select")}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Image alt="" src={item?.images?.[0]?.url} width={80} />
                }
                title={i18n.getPropNameByLocale(item, "name")}
                description={<SimpleProductInfo item={item} />}
              />
            </List.Item>
          )}
        />
      </div>
      <ScopeSelectionFooter
        selection={products}
        disabled={disabled}
        onRemove={removeSelected}
        onClear={clearSelected}
      />
    </Modal>
  );
}

export default connect((state) => ({ user: getUserInfo(state) }))(
  CouponProductSelectModal
);
